import { useCompanyProject, useProjectShipments, useShipmentEvents, useUserProfile } from '@/core/api/queries';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Shipment } from '@/core/api/types';
export interface TimelineShipment extends Shipment {
  title: string;
  startDate: Date;
  endDate: Date;
  carrier: string;
  reference: string;
  container: string | null;
  booking: string | null;
  bol: string | null;
}

const getDateValue = (shipment: any, field: 'eta' | 'etd' | 'ata' | 'atd'): Date | null => {
  // Try direct field first
  const directValue = shipment[field];
  if (directValue) {
    const date = new Date(directValue);
    if (!isNaN(date.getTime())) return date;
  }

  // Try customFields
  const customValue = shipment.customFields?.[field];
  if (customValue) {
    const date = new Date(customValue);
    if (!isNaN(date.getTime())) return date;
  }

  return null;
};

const parseShipmentDates = (shipment: any) => {
  let startDate = getDateValue(shipment, 'etd') || getDateValue(shipment, 'atd');
  let endDate = getDateValue(shipment, 'eta') || getDateValue(shipment, 'ata');

  // Fallback logic
  if (!startDate && !endDate) {
    startDate = new Date();
    endDate = dayjs().add(14, 'days').toDate();
  } else if (!startDate && endDate) {
    startDate = dayjs(endDate).subtract(1, 'day').toDate();
  } else if (startDate && !endDate) {
    endDate = dayjs(startDate).add(14, 'days').toDate();
  }

  return { startDate, endDate };
};

export const useTimelineShipments = () => {
  const [selectedShipment, setSelectedShipment] = useState<TimelineShipment | null>(null);

  const { data: userProfile } = useUserProfile();
  const companyId = userProfile?.companyId;
  const { data: project } = useCompanyProject(companyId);
  const { data: shipmentEvents } = useShipmentEvents(selectedShipment?.id || '');
  const { data: projectShipments, isLoading, error } = useProjectShipments(project?.id || '', false);

  const timelineShipments = useMemo(
    () =>
      projectShipments?.map((shipment: any) => {
        const { startDate, endDate } = parseShipmentDates(shipment);

        if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const container = shipment.customFields?.container || shipment.seaReferences?.containers?.[0] || null;

          const booking = shipment.customFields?.bookingNumber || null;

          const bol = shipment.customFields?.bolNumber || null;

          const title =
            container || booking || bol || shipment.internalReference || `Shipment ${shipment.id.slice(-6)}`;

          const status = shipment.status || shipment.customFields?.status || 'Unknown';

          const carrier =
            shipment.parties?.find((p: any) => p.role === 'carrier')?.name ||
            shipment.customFields?.carrier ||
            'Unknown';

          return {
            ...shipment,
            title,
            startDate,
            endDate,
            status,
            carrier,
            reference: container || booking || shipment.internalReference || '',
            container,
            booking,
            bol,
          };
        }
      }),
    [projectShipments],
  );

  return { timelineShipments, isLoading, error, selectedShipment, setSelectedShipment, shipmentEvents };
};
